import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';

const LOGO_URL = "/images/wayside-logo-horizontal.png";

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { itemCount } = useCart();
    const location = useLocation();

    const navLinks = [
        { to: '/', label: 'Home' },
        { to: '/shop', label: 'Shop' },
        { to: '/about', label: 'About' },
        { to: '/contact', label: 'Contact' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <header className="fixed top-0 left-0 w-full z-50 glass" data-testid="header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="flex justify-between items-center h-18 sm:h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center" data-testid="header-logo">
                        <img 
                            src={LOGO_URL} 
                            alt="Wayside Maple" 
                            className="h-8 sm:h-10 w-auto object-contain"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
                        {navLinks.map(link => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={`font-manrope text-sm uppercase tracking-widest transition-colors duration-300 ${
                                    isActive(link.to) 
                                        ? 'text-maple-red' 
                                        : 'text-maple-dark/70 hover:text-maple-dark'
                                }`}
                                data-testid={`nav-${link.label.toLowerCase()}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Right side */}
                    <div className="flex items-center gap-2 sm:gap-4">
                        <Link 
                            to="/cart" 
                            className="relative p-2.5 sm:p-3 hover:bg-black/5 transition-colors rounded-full text-maple-dark"
                            data-testid="cart-button"
                        >
                            <ShoppingBag className="w-5 h-5" />
                            {itemCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-maple-red text-white text-xs w-5 h-5 flex items-center justify-center font-mono">
                                    {itemCount}
                                </span>
                            )}
                        </Link>

                        {/* Mobile menu button */}
                        <button 
                            className="md:hidden p-2.5 sm:p-3 hover:bg-black/5 transition-colors rounded-full text-maple-dark"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            {isMenuOpen && (
                <nav className="md:hidden absolute top-[72px] sm:top-20 left-0 w-full bg-white border-t border-black/10 shadow-lg" data-testid="mobile-nav">
                    <div className="px-5 sm:px-6 py-7 sm:py-8 flex flex-col gap-5 sm:gap-6">
                        {navLinks.map(link => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={`font-anton text-[1.75rem] sm:text-2xl uppercase tracking-wider ${
                                    isActive(link.to) ? 'text-maple-red' : 'text-maple-dark'
                                }`}
                                onClick={() => setIsMenuOpen(false)}
                                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </nav>
            )}
        </header>
    );
};

export default Header;
